import { Box, Button, Modal, Typography } from "@mui/material";
import { SyntheticEvent, useCallback, useEffect, useMemo, useState } from "react";
import { UserPreference } from "../../interfaces/UserPreference";
import { FiltersChipsNames } from "../../enums/FiltersChipsNames";
import FilterChip from "../../views/filter-chip/FilterChip";
import { FilterNames } from "../../enums/FilterNames";
import PreferencesService from "../../services/preferences.service";
import { DataWrapper } from "../../interfaces/DataWrapper";
import { PostPreference } from "../../interfaces/PostPreference";
import NameHelper from "../../helpers/name.helper";
import { useErrorBoundary } from "react-error-boundary";
import { Filters } from "../../interfaces/Filters";
import classes from "./EditPreferencesModal.module.scss";
import FilterDropdownSearch from "../filter-dropdown-search/FilterDropdownSearch";
import { FilterOption } from "../../interfaces/FilterOption";
import { Tag } from "../../interfaces/Tag";
import TagsDropdown from "../tags-dropdown/TagsDropdown";
import StringHelper from "../../helpers/string.helper";
import TipsterPreferencesDropdown from "../tipster-dropdown/TipsterPreferencesDropdown";
import { Content } from "../../enums/Content";

const EditPreferencesModal = ({ open, onClose, preference }: { open: boolean, onClose: () => void, preference: UserPreference | null }) => {
  const [updatedPreference, setUpdatedPreference] = useState<UserPreference | null>(null);
  const { showBoundary } = useErrorBoundary();
  const onCloseHandler = () => {
    onClose();
  };
  const isSaveButtonDisabled = useMemo(() => {
    if (preference === updatedPreference) {
      return true;
    }

    if (updatedPreference?.sites.length === 0 && updatedPreference?.tags.length === 0 && updatedPreference?.types.length === 0) {
      return true;
    }

    return false;
  }, [updatedPreference, preference]);
  const tagValues: Tag[] = useMemo(() => {
    return updatedPreference?.tags.map((tag) => {
      return {
        id: tag.id,
        attributes: {
          name: tag.name,
          type: tag.type,
          tagger_id: tag.tagger_id,
          sport: tag.sport,
          gender: tag.gender
        }
      }
    }) ?? []
  }, [updatedPreference]);

  useEffect(() => {
    setUpdatedPreference(preference);
  }, [preference]);

  const updateTypesHandler = (event: SyntheticEvent<Element, Event>, value: FilterOption[]) => {
    setUpdatedPreference((prevUpdatedPreference) => {
      return prevUpdatedPreference ? { ...prevUpdatedPreference, types: value } : null;
    });
  };

  const updateSitesHandler = (event: SyntheticEvent<Element, Event>, value: FilterOption[]) => {
    setUpdatedPreference((prevUpdatedPreference) => {
      return prevUpdatedPreference ? { ...prevUpdatedPreference, sites: value } : null;
    });
  };

  const updateTipsterHandler = (hasTips: boolean | null) => {
    setUpdatedPreference((prevUpdatedPreference) => {
      return prevUpdatedPreference ? { ...prevUpdatedPreference, hasTips: hasTips } : null;
    });
  };

  const updateTagsHandler = (tags: Tag[]) => {
    setUpdatedPreference((prevUpdatedPreference) => {
      return prevUpdatedPreference ? {
        ...prevUpdatedPreference, tags: tags.map((tag) => {
          return {
            id: tag.id,
            name: tag.attributes.name,
            tagger_id: tag.attributes.tagger_id,
            type: tag.attributes.type,
            sport: tag.attributes.sport,
            gender: tag.attributes.gender
          }
        })
      } : null;
    });
  }

  const deleteHandler = (filterID: number, entity: FiltersChipsNames) => {
    switch (entity) {
      case FiltersChipsNames.TYPE:
        setUpdatedPreference((prevUpdatedPreference) => {
          return prevUpdatedPreference ? { ...prevUpdatedPreference, types: prevUpdatedPreference.types.filter((type) => type.id !== filterID) } : null;
        });
        break;
      case FiltersChipsNames.SITE:
        setUpdatedPreference((prevUpdatedPreference) => {
          return prevUpdatedPreference ? { ...prevUpdatedPreference, sites: prevUpdatedPreference.sites.filter((site) => site.id !== filterID) } : null;
        });
        break;
      case FiltersChipsNames.TAG:
        setUpdatedPreference((prevUpdatedPreference) => {
          return prevUpdatedPreference ? { ...prevUpdatedPreference, tags: prevUpdatedPreference.tags.filter((tag) => tag.id !== filterID) } : null;
        });
        break;
      case FiltersChipsNames.TIP:
        setUpdatedPreference((prevUpdatedPreference) => {
          return prevUpdatedPreference ? { ...prevUpdatedPreference, hasTips: null } : null;
        });
        break;
      default:
        break;
    }
  };

  const onSaveButtonHandler = useCallback(async () => {
    if (updatedPreference) {
      const filters: Filters = {
        hasWords: null,
        types: updatedPreference.types,
        sites: updatedPreference.sites,
        date: null,
        tags: updatedPreference.tags.map((tag) => ({ id: tag.id, attributes: { name: tag.name, tagger_id: tag.tagger_id, type: tag.type, sport: tag.sport, gender: tag.gender } })),
        author: null,
        hasTips: updatedPreference.hasTips
      }

      const putPreference: DataWrapper<Partial<PostPreference>> = {
        data: {
          name: NameHelper.createPreferenceNameFromFilters(filters),
          sites: updatedPreference.sites.map((site) => site.id),
          types: updatedPreference.types.map((type) => type.id),
          tags: updatedPreference.tags.map((tag) => tag.id),
          hasTips: updatedPreference.hasTips
        }
      };
      await PreferencesService.update(updatedPreference.id, putPreference)
        .then(() => onClose())
        .catch((error) => showBoundary(error));
    }
  }, [updatedPreference, showBoundary, onClose]);

  return <Modal open={open} onClose={onCloseHandler}>
    <Box className={`d-flex p-3 w-50 flex-column justify-content-between ${classes['modal-box']}`} >
      <Box className='d-flex flex-column gap-4'>
        <Typography className='h3'>
          Edit Filters
        </Typography>
        <Box className='d-flex gap-2 flex-wrap'>
          {updatedPreference?.types.map((type) => <FilterChip key={type.id} id={type.id} name={type.name} onDelete={deleteHandler} entity={FiltersChipsNames.TYPE} />)}
          {updatedPreference?.sites.map((site) => <FilterChip key={site.id} id={site.id} name={site.name} onDelete={deleteHandler} entity={FiltersChipsNames.SITE} />)}
          {updatedPreference?.tags.map((tag) => <FilterChip key={tag.id} id={tag.id} name={tag.name} onDelete={deleteHandler} entity={FiltersChipsNames.TAG} />)}
          {
            updatedPreference?.hasTips !== null ?
              <FilterChip id={0} key={StringHelper.generateRandomString(10)} name={updatedPreference?.hasTips ? 'With Tips' : 'Without Tips'} onDelete={deleteHandler} entity={FiltersChipsNames.TIP} />
              :
              null
          }
        </Box>
        <Box className='d-flex flex-column flex-sm-row gap-1 justify-content-between align-items-center'>
          <FilterDropdownSearch name={FilterNames.TYPE} value={updatedPreference?.types ?? []} setValue={updateTypesHandler} />
          <FilterDropdownSearch name={FilterNames.SITE} value={updatedPreference?.sites ?? []} setValue={updateSitesHandler} />
          <TagsDropdown content={Content.ARTICLES} tagValues={tagValues} setTagValues={updateTagsHandler} />
          <TipsterPreferencesDropdown hasTips={updatedPreference?.hasTips ?? null} setHasTips={updateTipsterHandler}/>
        </Box>
      </Box>
      <Box className='d-flex flex-column flex-sm-row mt-4 gap-4 w-100 justify-content-between'>
        <Button className={`${classes['discard-button']} w-100`} onClick={() => setUpdatedPreference(preference)}>
          Discard
        </Button>
        <Button className={`w-100 ${isSaveButtonDisabled ? classes['disabled-save-button'] : classes['save-button']}`} onClick={onSaveButtonHandler} disabled={isSaveButtonDisabled}>
          Save
        </Button>
      </Box>
    </Box>
  </Modal>
}

export default EditPreferencesModal;
